import {
  OnboardingClassStyle,
  OnboardingBookingLanguage,
  OnboardingEntrySource,
  OnboardingGoal,
  OnboardingLanguage,
  OnboardingPreferredTime,
  OnboardingStatus,
} from './schema'

export type OnboardingBookingSlot = {
  id: string
  label: string
  subtitle: string
  fullDate: string
  time: string
  utcClassDateTime?: string
}

export type OnboardingDraft = {
  status: OnboardingStatus
  entrySource: OnboardingEntrySource
  ownerUserId?: string | null
  language: OnboardingLanguage | null
  /** 예약 화면 전용 언어. JP 한국어(ko)를 레거시 EN/JP 설문 언어와 분리해 보존한다. */
  bookingLanguage?: OnboardingBookingLanguage | null
  goals: OnboardingGoal[]
  experienceLevel: 1 | 2 | 3 | 4 | 5 | null
  frequency: number | null
  preferredTime: OnboardingPreferredTime[]
  classStyle: OnboardingClassStyle[]
  selectedLevel: string | null
  recommendedLevel: string | null
  resultStyleTags: string[]
  resultLessonStyleTags: string[]
  selectedClassId: number | null
  prestudyClassId: number | null
  selectedClassDatetime: string | null
  trialBooked: boolean
  selectedSlot: OnboardingBookingSlot | null
  marketingAgreed: boolean | null
  trialSkipped: boolean
  showNotificationPrompt: boolean
  notificationResponded: boolean
  // 이전 온보딩을 완료한 유저가 다른 언어 배너로 재진입한 "추가 체험" 플로우.
  // frequency/preferredTime 은 이전 답변을 재사용하고, goal/experience/class-style 만 다시 묻는다.
  // 예약 후 마케팅 스텝도 건너뛴다.
  isReusedFlow?: boolean
}

export const ONBOARDING_STORAGE_KEY = 'podo:onboarding-v4'
export const ONBOARDING_COOKIE_KEY = 'podo-onboarding-v4'
export const ONBOARDING_ELIGIBLE_STORAGE_KEY = 'podo:onboarding-v4-eligible'
export const ONBOARDING_ELIGIBLE_COOKIE_KEY = 'podo-onboarding-v4-eligible'

export const DEFAULT_ONBOARDING_DRAFT: OnboardingDraft = {
  status: 'idle',
  entrySource: 'intro',
  ownerUserId: null,
  language: null,
  bookingLanguage: null,
  goals: [],
  experienceLevel: null,
  frequency: null,
  preferredTime: [],
  classStyle: [],
  selectedLevel: null,
  recommendedLevel: null,
  resultStyleTags: [],
  resultLessonStyleTags: [],
  selectedClassId: null,
  prestudyClassId: null,
  selectedClassDatetime: null,
  trialBooked: false,
  selectedSlot: null,
  marketingAgreed: null,
  trialSkipped: false,
  showNotificationPrompt: false,
  notificationResponded: false,
  isReusedFlow: false,
}

export type OnboardingOwnerUserId = string | number | null | undefined

const normalizeOnboardingOwnerUserId = (ownerUserId: OnboardingOwnerUserId) => {
  if (ownerUserId === null || ownerUserId === undefined || ownerUserId === '') {
    return null
  }

  return String(ownerUserId)
}

export const isOnboardingDraftOwnedByUser = (draft: OnboardingDraft, ownerUserId: OnboardingOwnerUserId) => {
  const normalizedOwnerUserId = normalizeOnboardingOwnerUserId(ownerUserId)

  if (!normalizedOwnerUserId || draft.status === 'idle') {
    return true
  }

  return draft.ownerUserId === normalizedOwnerUserId
}

export const getOnboardingDraftForUser = (
  draft: OnboardingDraft | null | undefined,
  ownerUserId: OnboardingOwnerUserId,
) => {
  if (!draft) {
    return DEFAULT_ONBOARDING_DRAFT
  }

  if (!isOnboardingDraftOwnedByUser(draft, ownerUserId)) {
    return DEFAULT_ONBOARDING_DRAFT
  }

  return draft
}

export const withOnboardingDraftOwner = (draft: OnboardingDraft, ownerUserId: OnboardingOwnerUserId) => {
  const normalizedOwnerUserId = normalizeOnboardingOwnerUserId(ownerUserId)

  if (!normalizedOwnerUserId || draft.status === 'idle') {
    return {
      ...draft,
      ownerUserId: null,
    }
  }

  return {
    ...draft,
    ownerUserId: normalizedOwnerUserId,
  }
}

export const getStartedOnboardingDraft = (draft: OnboardingDraft, entrySource: OnboardingEntrySource) => ({
  ...draft,
  status: 'in-progress' as const,
  entrySource,
})

export const shouldClearOnboardingDraftOnPageLeave = (
  _draft: OnboardingDraft,
  _options: { isInternalOnboardingNavigation: boolean },
) => false
